// =====================================================
// 📁 POSProductCardView.tsx
// =====================================================

import { useMemo } from 'react';

import { motion, AnimatePresence } from 'framer-motion';

import { ProductPanelItem } from '../../../../../types/productpanel/ProductPanelItem';

import useProductPanel from '../../../../../hooks/productpanel/useProductPanel';

import POSProductPlaceholder from '../POSProductPlaceholder';

import UniversalTablePagination from '../../../../Tables/universalRecord/UniversalTablePagination';

import POSProductItem from '../product-item/POSProductItem';

interface Props {
  onAddProductClick: (product: ProductPanelItem) => void;
  mode?: 'list' | 'card';
}

const POSProductCardView: React.FC<Props> = ({ onAddProductClick, mode }) => {
  // =====================================================
  // 🔹 Hooks
  // =====================================================
  const {
    filteredProducts,

    paginator,

    pageSize,

    fetchFilteredProducts,

    currentFilter,

    updatePageSize,

    updateCurrentPage,
  } = useProductPanel();

  // =====================================================
  // 🔹 Pagination
  // =====================================================
  const handlePageChange = (page: number) => {
    updateCurrentPage(page);

    fetchFilteredProducts(currentFilter || {}, page, pageSize);
  };

  const handlePageSizeChange = (size: number) => {
    updatePageSize(size);

    updateCurrentPage(0);

    fetchFilteredProducts(currentFilter || {}, 0, size);
  };

  // =====================================================
  // 🔹 Unique Products
  // =====================================================
  const uniqueProducts = useMemo(() => {
    return filteredProducts.filter(
      (p, index, self) =>
        index === self.findIndex((q) => q.id === p.id && q.name === p.name)
    );
  }, [filteredProducts]);

  // =====================================================
  // 🔹 Empty
  // =====================================================
  if (uniqueProducts.length === 0) {
    return <POSProductPlaceholder mode={mode} />;
  }

  // =====================================================
  // 🎨 Render
  // =====================================================
  return (
    <div className="space-y-4">
      {/* ===================================================== */}
      {/* 🔹 Grid */}
      {/* ===================================================== */}

      <div
        className="
        bg-gray-1

        rounded-2xl

        p-3

        dark:bg-boxdark-2

        sm:p-4
      "
      >
        <div
          className="
          grid

          grid-cols-2

          gap-3

          sm:grid-cols-2

          md:grid-cols-3

          lg:grid-cols-4

          xl:grid-cols-4

          2xl:grid-cols-4
        "
        >
          <AnimatePresence>
            {uniqueProducts.map((product, index) => (
              <motion.div
                key={`${product.id}-${index}`}
                layout
                initial={{
                  opacity: 0,
                  scale: 0.9,
                  y: 15,
                }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  y: 0,
                }}
                exit={{
                  opacity: 0,
                  scale: 0.9,
                  y: 10,
                }}
                transition={{
                  duration: 0.2,
                  ease: 'easeOut',
                }}
              >
                <POSProductItem
                  product={product}
                  mode="card"
                  onAddToCart={onAddProductClick}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      {/* ===================================================== */}
      {/* 🔹 Pagination */}
      {/* ===================================================== */}

      <div className="w-full overflow-x-auto">
        <UniversalTablePagination
          paginator={paginator}
          data={uniqueProducts}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>
    </div>
  );
};

export default POSProductCardView;
